import React, { useId, useState } from 'react';
import type { McpServerCandidate } from '../../types';

interface McpCredentialFieldsProps {
  candidate: McpServerCandidate;
  onChange: (credentials: { name: string; value: string }[]) => void;
}

/**
 * The env-var name the entered secret is stored under. Remote MCP servers
 * almost always take a single bearer token / API key, injected into the
 * request header at connection time (never persisted in plaintext config).
 * The shape is an array so multi-field auth can be added later without changing
 * callers; the backend `_build_env_vars` maps each {name,value} into an
 * encrypted env var on provision.
 */
const CREDENTIAL_ENV_NAME = 'MCP_API_KEY';

/**
 * McpCredentialFields (AC-1738, updated AC-1800; epic AC-1732 / AC-1799). Collects
 * the credential a remote (streamable-http) MCP server needs to authenticate,
 * AFTER the user has seen the approval intent.
 *
 * AC-1800 contract change: a credential input is now ALWAYS offered for a remote
 * candidate so the user can supply a token even when the registry did not declare
 * `requires_auth`. The backend now sets `requires_auth=true` for known
 * credentialed services (slack/notion/etc), in which case the field is REQUIRED;
 * otherwise it is an OPTIONAL "API key / token (optional)" field.
 *
 * Behavior:
 *   - When `candidate.requires_auth` is true: renders a REQUIRED secret input
 *     (type="password", aria-required) labelled "API key / token".
 *   - Otherwise: renders an OPTIONAL secret input labelled
 *     "API key / token (optional)".
 *   - The field is NEVER pre-filled. Typing emits the credentials array (the
 *     shape sent on the approve request); an empty value emits an empty array so
 *     a no-auth, no-token approval still provisions cleanly. The value lives only
 *     in local state + the parent's request and is never logged.
 */
const McpCredentialFields: React.FC<McpCredentialFieldsProps> = ({
  candidate,
  onChange,
}) => {
  const fieldId = useId();
  const [value, setValue] = useState('');

  const requiresAuth = !!candidate.requires_auth;

  const handleChange = (next: string) => {
    setValue(next);
    // Empty value -> empty credentials array, so an optional field left blank
    // provisions with no env vars (mirrors the backend _build_env_vars no-auth
    // path). A non-empty value carries the single token credential.
    onChange(
      next ? [{ name: CREDENTIAL_ENV_NAME, value: next }] : [],
    );
  };

  const label = requiresAuth
    ? 'API key / token'
    : 'API key / token (optional)';

  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={fieldId}
        className="text-xs font-medium text-gray-700 dark:text-gray-300"
      >
        {label}
        {requiresAuth && <span className="text-red-500"> *</span>}
      </label>
      <input
        id={fieldId}
        type="password"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        autoComplete="off"
        spellCheck={false}
        required={requiresAuth}
        aria-required={requiresAuth ? 'true' : undefined}
        placeholder={
          requiresAuth
            ? `Enter the ${candidate.display_name} API key`
            : `Optional: ${candidate.display_name} API key / token`
        }
        className="min-h-[44px] w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-400"
      />
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {requiresAuth
          ? 'Authentication is required. Sent only when you approve, then stored encrypted. Never logged.'
          : 'Optional. If supplied, it is sent only when you approve, then stored encrypted. Never logged.'}
      </p>
    </div>
  );
};

export default McpCredentialFields;
